import useMediaQuery from "@/lib/hooks/use-media-query";
import { cn } from "@/lib/utils";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { createContext, useContext, type ComponentProps } from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import Button, { IconButton } from "../button/button";
import X from "../icons/x";

const AlertDialogContext = createContext(
  {} as {
    open?: boolean;
    isDrawer?: boolean;
    onOpenChange?: (open: boolean) => void;
  },
);

function useAlertDialog() {
  return useContext(AlertDialogContext);
}

export default function AlertDialog({
  open,
  onOpenChange,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Root> &
  ComponentProps<typeof DrawerPrimitive.Root>) {
  const isDialog = useMediaQuery("md");
  const Comp = isDialog ? AlertDialogPrimitive.Root : DrawerPrimitive.Root;

  return (
    <AlertDialogContext.Provider
      value={{ open, isDrawer: !isDialog, onOpenChange }}
    >
      <Comp
        open={open}
        data-slot="alert-dialog"
        onOpenChange={onOpenChange}
        {...props}
      />
    </AlertDialogContext.Provider>
  );
}

export function AlertDialogTrigger({
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Trigger> &
  ComponentProps<typeof DrawerPrimitive.Trigger>) {
  const { isDrawer } = useAlertDialog();
  const Comp = isDrawer
    ? DrawerPrimitive.Trigger
    : AlertDialogPrimitive.Trigger;

  return <Comp data-slot="alert-dialog-trigger" {...props} />;
}

export function AlertDialogClose({
  asChild,
  children,
  className,
  ...props
}: ComponentProps<typeof IconButton>) {
  const { isDrawer } = useAlertDialog();
  const Comp = isDrawer ? DrawerPrimitive.Close : AlertDialogPrimitive.Cancel;

  if (asChild) {
    return <Comp asChild>{children}</Comp>;
  }

  if (isDrawer) {
    return null;
  }

  return (
    <Comp asChild>
      <button
        className={cn(
          "interactive has-icon has-icon-size-4 text-foreground-pressed hover:opacity-hovered active:opacity-pressed absolute top-4 right-4 cursor-pointer rounded-xs",
          className,
        )}
        {...props}
      >
        {children ?? <X />}
      </button>
    </Comp>
  );
}

export function AlertDialogPortal({
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  const { isDrawer } = useAlertDialog();
  const Comp = isDrawer ? DrawerPrimitive.Portal : AlertDialogPrimitive.Portal;

  return <Comp data-slot="alert-dialog-portal" {...props} />;
}

export function AlertDialogOverlay({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  const { isDrawer } = useAlertDialog();
  const Comp = isDrawer
    ? DrawerPrimitive.Overlay
    : AlertDialogPrimitive.Overlay;

  return (
    <Comp
      data-slot="alert-dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

export function AlertDialogContent({
  children,
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Content> &
  ComponentProps<typeof DrawerPrimitive.Content>) {
  const { isDrawer } = useAlertDialog();
  const Comp = isDrawer
    ? DrawerPrimitive.Content
    : AlertDialogPrimitive.Content;

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <Comp
        data-slot="alert-dialog-content"
        className={cn(
          isDrawer
            ? [
                "group/drawer-content bg-background fixed z-50 flex h-auto flex-col px-4 pt-12 pb-6",
                "data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-lg data-[vaul-drawer-direction=top]:border-b",
                "data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh] data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=bottom]:border-t",
                "data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=right]:sm:max-w-sm",
                "data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:border-r data-[vaul-drawer-direction=left]:sm:max-w-sm",
              ]
            : [
                // the same frame as the settings cards and the dialogs
                "bg-background border-border text-foreground fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-5 rounded-xl border p-5 shadow-xl duration-200 sm:max-w-md",
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              ],
          className,
        )}
        {...props}
      >
        {isDrawer && (
          // The handle for the drawer
          <div className="bg-background-neutral-disabled absolute top-5 left-1/2 mx-auto hidden h-1.5 w-12 shrink-0 -translate-x-1/2 rounded-full group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
        )}
        {children}
      </Comp>
    </AlertDialogPortal>
  );
}

export function AlertDialogHeader({
  className,
  ...props
}: ComponentProps<"div">) {
  const { isDrawer } = useAlertDialog();

  return (
    <div
      data-slot="alert-dialog-header"
      className={cn(
        isDrawer
          ? "flex flex-col gap-3 text-left"
          : "flex flex-col gap-1 text-left",
        className,
      )}
      {...props}
    />
  );
}

export function AlertDialogFooter({
  className,
  ...props
}: ComponentProps<"div">) {
  const { isDrawer } = useAlertDialog();

  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        isDrawer
          ? "flex items-center justify-stretch gap-2 pt-4"
          : "flex items-center justify-end gap-2",
        className,
      )}
      {...props}
    />
  );
}

export function AlertDialogTitle({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Title> &
  ComponentProps<typeof DrawerPrimitive.Title>) {
  const { isDrawer } = useAlertDialog();
  const Comp = isDrawer ? DrawerPrimitive.Title : AlertDialogPrimitive.Title;

  return (
    <Comp
      data-slot="alert-dialog-title"
      className={cn(
        "text-base leading-tight font-semibold",
        isDrawer && "text-center",
        className,
      )}
      {...props}
    />
  );
}

export function AlertDialogDescription({
  className,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Description> &
  ComponentProps<typeof DrawerPrimitive.Description>) {
  const { isDrawer } = useAlertDialog();
  const Comp = isDrawer
    ? DrawerPrimitive.Description
    : AlertDialogPrimitive.Description;

  return (
    <Comp
      data-slot="alert-dialog-description"
      className={cn(
        "text-foreground-pressed text-sm",
        isDrawer && "text-center",
        className,
      )}
      {...props}
    />
  );
}

export function AlertDialogAction({
  className,
  ...props
}: ComponentProps<typeof Button>) {
  const { isDrawer } = useAlertDialog();
  // full width in the phone drawer, small at the right in the dialog
  const button = (
    <Button
      size={isDrawer ? undefined : "sm"}
      className={cn(isDrawer && "flex-1", className)}
      {...props}
    />
  );

  return isDrawer ? (
    button
  ) : (
    <AlertDialogPrimitive.Action asChild>{button}</AlertDialogPrimitive.Action>
  );
}

export function AlertDialogCancel({
  className,
  ...props
}: ComponentProps<typeof Button>) {
  const { isDrawer } = useAlertDialog();
  const Comp = isDrawer ? DrawerPrimitive.Close : AlertDialogPrimitive.Cancel;

  return (
    <Comp asChild>
      <Button
        variant={isDrawer ? "secondary" : "ghost"}
        size={isDrawer ? undefined : "sm"}
        className={cn(isDrawer && "flex-1", className)}
        {...props}
      />
    </Comp>
  );
}
